import { LoadingButton } from '@mui/lab';
import { Box, Grid, Paper, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { ClientSideDataTable } from 'src/common/components/dataTable/clientSide';
import { DatatableColumn } from 'src/common/components/dataTable/types';
import { Props } from '../../container';
import { ClientCsvRow, ClientSearchResult } from '../../store/types';

const displayType = (result?: ClientSearchResult): string => result?.accountType ?? result?.clientType ?? 'Group';

interface ReportingGenerationClientTableProps
  extends Pick<Props, 'bulk' | 'reporting' | 'fetchFilteredReportClientsProgress' | 'createBulkReportProgress' | 'createBulkReport'> {
  setSelectedClientIdRows: (ids: Set<number>) => void;
  setSelectedClientRows: (ids: number[]) => void;
  setSelectedGroupRows: (ids: number[]) => void;
  setFlagReset: (value: boolean) => void;
  flagReset: boolean;
}

export const ReportingGenerationClientTable = (props: ReportingGenerationClientTableProps): JSX.Element => {
  const [selectedClientIds, setSelectedClientIds] = useState<Set<number>>(new Set());
  const [selectedClientRows, setSelectedClientRows] = useState<number[]>([]);
  const [selectedGroupRows, setSelectedGroupRows] = useState<number[]>([]);

  const handleNext = () => {
    props.setSelectedClientIdRows(selectedClientIds);
    props.setSelectedClientRows(selectedClientRows);
    props.setSelectedGroupRows(selectedGroupRows);
    props.createBulkReport({
      clientOrGroupIds: [...selectedClientRows, ...selectedGroupRows],
      runImmediately: false,
      addToExistingClientOrGroupIds: false,
      allowTerminatedClients: props.bulk.allowTerminatedClient,
    });
  };

  const clientNameColumn = (dataIndex: number, results: ClientSearchResult[]): React.ReactNode => (
    <Typography variant="h5">{results[dataIndex]?.clientName}</Typography>
  );

  const typeColumn = (dataIndex: number, results: ClientSearchResult[]): React.ReactNode => (
    <Typography variant="h5">{displayType(results[dataIndex])}</Typography>
  );

  const getResultColumns = (results: ClientSearchResult[]): DatatableColumn[] => [
    {
      name: 'clientId',
      label: 'ID',
      options: {
        display: 'false',
        filter: false,
        sort: true,
        download: true,
        viewColumns: false,
      },
    },
    {
      name: 'clientName',
      label: 'NAME',
      options: {
        filter: false,
        sort: true,
        download: true,
        customBodyRenderLite: (dataIndex: number) => clientNameColumn(dataIndex, results),
      },
    },
    {
      name: 'clientType',
      label: 'TYPE',
      options: {
        filter: false,
        sort: true,
        download: true,
        customBodyRenderLite: (dataIndex) => typeColumn(dataIndex, results),
      },
    },
  ];

  const clientOrGroupSearchResults: ClientSearchResult[] = useMemo(() => {
    const results: ClientSearchResult[] = [
      ...props.bulk.selectedGroups.map((group) => {
        return {
          clientId: group.entityCoreId,
          clientName: group.name,
          clientType: 'Group',
        } as ClientSearchResult;
      }),
      ...props.bulk.selectedClients.map((client) => {
        return {
          clientId: client.entityCoreId,
          clientName: client.name,
          clientType: 'Client',
          accountType: client.accountType,
        } as ClientSearchResult;
      }),
      ...props.bulk.importedClients,
      ...props.bulk.clients.map((client) => ({
        clientId: client.entityCoreId,
        clientName: client.name,
        clientType: 'Client',
        accountType: client.accountType,
      })),
      ...props.bulk.groups.map((group) => ({ clientId: group.entityCoreId, clientName: group.name, clientType: 'Group' })),
    ];
    const uniqueMap = new Map<number, ClientSearchResult>();
    for (const item of results) {
      if (!uniqueMap.has(item.clientId)) {
        uniqueMap.set(item.clientId, item);
      }
    }
    return Array.from(uniqueMap.values());
  }, [props.bulk.selectedGroups, props.bulk.selectedClients, props.bulk.clients, props.bulk.groups, props.bulk.importedClients]);

  const selectedRowIndices = useMemo(() => {
    const indices: number[] = [];
    clientOrGroupSearchResults.forEach((result, index) => {
      if (selectedClientIds.has(result.clientId)) {
        indices.push(index);
      }
    });
    return indices;
  }, [clientOrGroupSearchResults, selectedClientIds]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleDownload = (buildHead: (columns: any) => string, buildBody: (data: any) => string, columns: any) => {
    const mappedRows = clientOrGroupSearchResults.map((result, index) => {
      return {
        data: [result.clientId, result.clientName, displayType(result)],
        index: index,
      } as ClientCsvRow;
    });
    const filteredData = selectedRowIndices.length > 0 ? mappedRows.filter((_, idx) => selectedRowIndices.includes(idx)) : mappedRows;
    return buildHead(columns) + buildBody(filteredData);
  };

  useEffect(() => {
    if (clientOrGroupSearchResults.length === 0 || props.flagReset) {
      setSelectedClientIds(new Set());
      setSelectedClientRows([]);
      setSelectedGroupRows([]);

      props.setFlagReset(false);
    }
  }, [clientOrGroupSearchResults, props.flagReset]);

  useEffect(() => {
    setSelectedClientRows(props.reporting.bulk.selectedClientRows);
    setSelectedGroupRows(props.reporting.bulk.selectedGroupRows);
    setSelectedClientIds(props.reporting.bulk.selectedClientIdRows);
  }, [props.reporting.bulk.selectedClientRows, props.reporting.bulk.selectedGroupRows, props.reporting.bulk.selectedClientIdRows]);

  return (
    <Grid item container direction="column" xs={12} sm={8} mt={2}>
      <Paper elevation={0}>
        <ClientSideDataTable
          loadingProgress={props.fetchFilteredReportClientsProgress}
          columns={getResultColumns(clientOrGroupSearchResults)}
          data={clientOrGroupSearchResults}
          options={{
            filter: false,
            selectableRows: 'multiple',
            onDownload: handleDownload,
            selectableRowsHideCheckboxes: false,
            download: true,
            downloadOptions: {
              filename: 'Clients.csv',
            },
            onRowSelectionChange: (currentRowsSelected, allRowsSelected, rowsSelected) => {
              const selectedIndicesSet = new Set(rowsSelected || []);
              const { clients, groups, clientIds } = clientOrGroupSearchResults.reduce(
                (acc, result, index) => {
                  if (selectedIndicesSet.has(index)) {
                    acc.clientIds.add(result.clientId);
                    if (result.clientType === 'Group') {
                      acc.groups.push(result.clientId);
                    } else {
                      acc.clients.push(result.clientId);
                    }
                  }
                  return acc;
                },
                { clients: [] as number[], groups: [] as number[], clientIds: new Set<number>() }
              );

              setSelectedClientRows(clients);
              setSelectedGroupRows(groups);
              setSelectedClientIds(clientIds);
            },
            rowsSelected: selectedRowIndices,
          }}
          enablePagination
        />
        <Box display="flex" flexDirection="row" justifyContent="flex-end" padding="24px">
          <LoadingButton
            loading={props.createBulkReportProgress.isLoading}
            disabled={selectedRowIndices.length <= 0}
            disableElevation
            color="primary"
            onClick={handleNext}
            variant="contained"
          >
            Next
          </LoadingButton>
        </Box>
      </Paper>
    </Grid>
  );
};
